import { useDispatch, useSelector } from 'react-redux'
import { constants, prorithmMessageTypes } from '../../utils/constants'
import { formatReadableDate, getCurrentDateAndTime } from '../../utils/utils'
import {
  useRef,
  ReactNode,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react'
import {
  addProrithmNotification,
  setProrithmWebSocketCount,
  setProrithmWebSocketMessage,
  setProrithmWebSocketConnectionStatus,
} from '../../redux/actions'
import {
  prorithmWebSocketService,
  ProrithmWebSocketMessage,
} from './websocketServices'

interface ProrithmWebSocketContextType {
  isConnected: boolean
  connectionStatus: string
  messageCount: number
  lastMessage: any
  manualConnect: () => void
}

const ProrithmWebSocketContext = createContext<
  ProrithmWebSocketContextType | undefined
>(undefined)

interface ProrithmWebSocketProviderProps {
  children: ReactNode
}

export const ProrithmWebSocketProvider: React.FC<
  ProrithmWebSocketProviderProps
> = ({ children }) => {
  const dispatch = useDispatch()
  const isConnected = useSelector(
    (state: any) => state.prorithmWebSocketConnectionStatus
  )
  const messageCount = useSelector((state: any) => state.prorithmWebSocketCount)
  const lastMessage = useSelector(
    (state: any) => state.prorithmWebSocketMessage
  )
  const connectionStatus = useSelector((state: any) =>
    state.prorithmWebSocketConnectionStatus
      ? constants?.connected
      : constants?.disconnected
  )

  const isInitializedRef = useRef<boolean>(false)
  const recentNotificationsRef = useRef<Set<string>>(new Set())
  const timeoutIdsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  const handleWebSocketMessage = useCallback(
    (message: ProrithmWebSocketMessage) => {
      // Store the message in Redux
      dispatch(setProrithmWebSocketMessage(message))

      // Increment count for ADD, UPDATE, DELETE messages
      if (prorithmMessageTypes?.includes(message?.messageType)) {
        dispatch(setProrithmWebSocketCount())

        // unique key for this notification to prevent duplicates
        const notificationKey = `${message?.messageContent?.deviceId}-${
          message?.messageType
        }-${Date.now()}`

        // Check if this is a recent duplicate (within 5 seconds)
        const isDuplicate =
          recentNotificationsRef?.current?.has(notificationKey)

        if (!isDuplicate) {
          // Add to recent notifications set
          recentNotificationsRef?.current?.add(notificationKey)

          // Clear any existing timeout for this key
          if (timeoutIdsRef.current.has(notificationKey)) {
            clearTimeout(timeoutIdsRef.current.get(notificationKey))
          }

          // Remove from set after 5 seconds
          const timeoutId = setTimeout(() => {
            recentNotificationsRef.current.delete(notificationKey)
            timeoutIdsRef.current.delete(notificationKey)
          }, 5000)

          timeoutIdsRef.current.set(notificationKey, timeoutId)

          // Add notification to the list
          const notification = {
            id: crypto.randomUUID(),
            messageType: message?.messageType,
            messageContent: message?.messageContent,
            timestamp: formatReadableDate(getCurrentDateAndTime()),
            isRead: false,
          }

          dispatch(addProrithmNotification(notification))
        }
      }
    },
    [dispatch]
  )

  const handleWebSocketOpen = useCallback(() => {
    dispatch(setProrithmWebSocketConnectionStatus(true))
  }, [dispatch])

  const handleWebSocketClose = useCallback(() => {
    dispatch(setProrithmWebSocketConnectionStatus(false))
  }, [dispatch])

  const handleWebSocketError = useCallback(
    (error: Event) => {
      dispatch(setProrithmWebSocketConnectionStatus(false))
      console.error(error)
    },
    [dispatch]
  )

  const initializeWebSocket = useCallback(() => {
    if (isInitializedRef.current) {
      // WebSocket already initialized
      return
    }

    prorithmWebSocketService.connect({
      onMessage: handleWebSocketMessage,
      onOpen: handleWebSocketOpen,
      onClose: handleWebSocketClose,
      onError: handleWebSocketError,
    })

    // Socket connection established
    isInitializedRef.current = true
  }, [
    handleWebSocketMessage,
    handleWebSocketOpen,
    handleWebSocketClose,
    handleWebSocketError,
  ])

  useEffect(() => {
    initializeWebSocket()

    return () => {
      prorithmWebSocketService.disconnect()
      isInitializedRef.current = false
    }
  }, [initializeWebSocket])

  const contextValue: ProrithmWebSocketContextType = {
    isConnected,
    connectionStatus,
    messageCount,
    lastMessage,
    manualConnect: initializeWebSocket,
  }

  return (
    <ProrithmWebSocketContext.Provider value={contextValue}>
      {children}
    </ProrithmWebSocketContext.Provider>
  )
}

export const useProrithmWebSocket = (): ProrithmWebSocketContextType => {
  const context = useContext(ProrithmWebSocketContext)
  if (context === undefined) {
    return {} as ProrithmWebSocketContextType
  }
  return context
}
